import { useEffect, useRef } from "react";

/** Auto-growing textarea (1 line → maxLines). With `fill`, stretch to the row
 *  height instead (used when the mini map sits alongside the notes). */
export function useAutoGrow(value: string, maxLines = 2, fill = false) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (fill) {
      el.style.height = "100%";
      el.style.overflowY = "auto";
      return;
    }
    el.style.height = "auto";
    const cs = getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || 18;
    const padV = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const max = line * maxLines + padV + 2;
    el.style.height = Math.min(el.scrollHeight, max) + "px";
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [value, maxLines, fill]);
  return ref;
}

/** Auto-width input (grows to fit content, capped). */
export function useAutoWidth(value: string, min = 70, max = 300) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.width = "0px";
    const w = Math.min(max, Math.max(min, el.scrollWidth + 2));
    el.style.width = w + "px";
  }, [value, min, max]);
  return ref;
}
